import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import WishlistButton from "@/components/WishlistButton";
import { useConcierge } from "@/context/ConciergeContext";
import { COLLECTIONS, collectionBySlug } from "@/config/collections";
import { distinctSizes } from "@/lib/sizes";
import { ArrowRight } from "lucide-react";

const CAT_SLUG = { footwear: "footwear", apparel: "apparel", sports: "sports-equipment" };
const PRICE_BANDS = [
  { k: "u1000", label: "Under ₹1,000", min: 0, max: 1000 },
  { k: "1000-2500", label: "₹1,000 – ₹2,500", min: 1000, max: 2500 },
  { k: "2500-5000", label: "₹2,500 – ₹5,000", min: 2500, max: 5000 },
  { k: "5000+", label: "₹5,000 & above", min: 5000, max: Infinity },
];
const SORTS = [
  { k: "recommended", label: "Recommended" },
  { k: "price_asc", label: "Price: Low → High" },
  { k: "price_desc", label: "Price: High → Low" },
  { k: "newest", label: "Newest" },
];

function FilterGroup({ label, children }) {
  return (
    <div className="py-5 border-b border-foreground/15">
      <p className="font-mono text-[10px] uppercase tracking-widest mb-3">{label}</p>
      {children}
    </div>
  );
}
function Check({ checked, onChange, children }) {
  return (
    <label className="flex items-center gap-2.5 py-1 text-sm cursor-pointer select-none hover:text-[hsl(var(--accent))]">
      <input type="checkbox" checked={checked} onChange={onChange} className="w-3.5 h-3.5 accent-[hsl(var(--accent))]" />
      {children}
    </label>
  );
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { profile, openConcierge } = useConcierge();
  const category = params.get("category") || "";
  const categoryId = params.get("category_id") || "";
  const subId = params.get("subcategory_id") || "";
  const department = params.get("department") || "";
  const collection = collectionBySlug(params.get("collection") || "");
  const picks = params.get("picks") === "1" && profile?.completed;
  const [q, setQ] = useState(params.get("q") || "");

  // Client-side refinement state (instant, no reload).
  const [sizes, setSizes] = useState(new Set());
  const [band, setBand] = useState("");
  const [certifiedOnly, setCertifiedOnly] = useState(false);
  const [bisOnly, setBisOnly] = useState(false);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [sort, setSort] = useState("recommended");

  useEffect(() => { api.get("/categories", { params: { in_stock: true } }).then(r => setCats(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    let alive = true; setLoading(true);
    const p = {};
    for (const k of ["q", "category", "category_id", "subcategory_id", "department"]) { const v = params.get(k); if (v) p[k] = v; }
    api.get("/products", { params: p })
      .then(({ data }) => { if (alive) { setProducts(data || []); setLoading(false); } })
      .catch(() => { if (alive) { setProducts([]); setLoading(false); } });
    return () => { alive = false; };
  }, [params]);

  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => { v ? next.set(k, v) : next.delete(k); });
    setParams(next);
  };
  const applySearch = (e) => { e.preventDefault(); update({ q }); setFiltersOpen(false); };
  const selectCategory = (slug) => { update({ category: slug, category_id: "", subcategory_id: "" }); setFiltersOpen(false); };
  const toggleSize = (s) => setSizes((prev) => { const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n; });
  const clearAll = () => { setSizes(new Set()); setBand(""); setCertifiedOnly(false); setBisOnly(false); setInStockOnly(false); setQ(""); update({ q: "", max_price: "" }); };

  // Category names for the dynamic heading.
  const catById = {}; const subById = {};
  cats.forEach((c) => { catById[c.id] = c; (c.subcategories || []).forEach((s) => { subById[s.id] = s; }); });
  const heading = (subId && subById[subId]?.name)
    || (categoryId && catById[categoryId]?.name)
    || (category && category.replace(/-/g, " ").replace(/^./, (c) => c.toUpperCase()))
    || department || "Everything";

  // Client-side filter + sort. Collections and "Your picks" layer extra rules.
  const shown = useMemo(() => {
    const bandDef = PRICE_BANDS.find((b) => b.k === band);
    const pickCat = picks ? CAT_SLUG[profile.category] : null;
    let list = products.filter((p) => {
      if (certifiedOnly && !p.spotted_certificate?.passed) return false;
      if (bisOnly && p.category !== "footwear") return false;
      if (inStockOnly && !(Number(p.stock) > 0)) return false;
      if (sizes.size && !(p.sizes || []).some((s) => sizes.has(String(s)))) return false;
      if (bandDef) { const pr = Number(p.price || 0); if (pr < bandDef.min || pr >= bandDef.max) return false; }
      if (collection && !collection.match(p)) return false;
      if (picks) {
        if (pickCat && p.category !== pickCat) return false;
        // Scope to the chosen gender's department (footwear/apparel only) so Women
        // picks never include Men listings; Unisex and untagged items stay eligible.
        if (profile.category === "footwear" || profile.category === "apparel") {
          const pd = String(p.department || "").toLowerCase();
          if (pd && pd !== "unisex" && pd !== (profile.gender || "men")) return false;
        }
        if (profile.size && (p.sizes || []).length && !(p.sizes || []).map(String).includes(String(profile.size))) return false;
        if (profile.price && !profile.price.any) { const pr = Number(p.price || 0); if (pr < profile.price.min || pr > profile.price.max) return false; }
      }
      return true;
    });
    // Effective sort: an explicit pick wins; else a collection's default; else Recommended.
    const key = sort !== "recommended" ? sort : (collection?.sort || (picks ? "rating" : "recommended"));
    const by = {
      price_asc: (a, b) => a.price - b.price, price_desc: (a, b) => b.price - a.price,
      newest: (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
      rating: (a, b) => (Number(b.rating) || 0) - (Number(a.rating) || 0),
    }[key];
    if (by) list = [...list].sort(by);
    return list;
  }, [products, sizes, band, certifiedOnly, bisOnly, inStockOnly, sort, collection, picks, profile]);

  // Heading + framing for Everything / a category / a collection / your picks.
  const pickChips = picks ? [profile.use, ...(profile.priorities || []), profile.price && !profile.price.any ? profile.price.t : null, profile.size ? `Size ${profile.size}` : null].filter(Boolean) : [];
  const eyebrow = picks ? "The Shop · Personalized" : collection ? "The Shop · Collection" : department ? `The Shop · ${department}` : "The Shop";
  const title = picks ? "Your picks" : collection ? collection.title : heading;
  const subtitle = collection?.subtitle || "";

  const activeFilters = sizes.size + (band ? 1 : 0) + (certifiedOnly ? 1 : 0) + (bisOnly ? 1 : 0) + (inStockOnly ? 1 : 0);

  // Size chips reflect what's actually in the current result set.
  const sizeOptions = useMemo(() => distinctSizes(products), [products]);

  return (
    <div className="max-w-[1760px] mx-auto px-4 md:px-12 py-6 md:py-12">
      {/* Mobile: Filter · Sort */}
      <div className="flex items-center gap-3 mb-6 md:hidden">
        <button onClick={() => setFiltersOpen((o) => !o)} className="flex-1 border border-foreground px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">
          {filtersOpen ? "Hide filters" : `Filter${activeFilters ? ` · ${activeFilters}` : ""}`}
        </button>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="flex-1 border border-foreground bg-background px-3 py-2.5 font-mono text-[10px] uppercase tracking-widest">
          {SORTS.map((s) => <option key={s.k} value={s.k}>Sort · {s.label}</option>)}
        </select>
      </div>

      <div className="grid md:grid-cols-12 gap-8 md:gap-12">
        {/* Sticky filter rail */}
        <aside className={`${filtersOpen ? "block" : "hidden"} md:block md:col-span-3 lg:col-span-2 md:sticky md:top-6 md:self-start md:max-h-[calc(100vh-3rem)] md:overflow-y-auto md:border-r border-foreground/20 md:pr-8`}>
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">The Filters</p>
            {(activeFilters > 0 || q) && (
              <button onClick={clearAll} className="font-mono text-[9px] uppercase tracking-widest text-[hsl(var(--accent))] hover:underline">Clear all</button>
            )}
          </div>

          <form onSubmit={applySearch} className="mt-3 mb-1">
            <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products, brands…" className="underline-input w-full" />
          </form>

          <FilterGroup label="Category">
            <div className="flex flex-col">
              {[{ s: "", n: "Everything" }, ...cats.map((c) => ({ s: c.slug, n: c.name }))].map((c) => (
                <button key={c.s} data-testid={`filter-cat-${c.s || "all"}`} onClick={() => selectCategory(c.s)}
                  className={`text-left py-1 text-sm ${(c.s ? category === c.s : !category && !categoryId && !subId) ? "text-[hsl(var(--accent))]" : "hover:text-[hsl(var(--accent))]"}`}>
                  {c.n}
                </button>
              ))}
            </div>
          </FilterGroup>

          <FilterGroup label="Size">
            <div className="flex flex-wrap gap-2">
              {sizeOptions.length === 0 && <p className="text-xs text-muted-foreground">—</p>}
              {sizeOptions.map((s) => (
                <button key={s} onClick={() => toggleSize(s)}
                  className={`min-w-9 h-9 px-2 border font-mono text-xs ${sizes.has(s) ? "bg-foreground text-background border-foreground" : "border-foreground/30 hover:border-foreground"}`}>{s}</button>
              ))}
            </div>
          </FilterGroup>

          <FilterGroup label="Price">
            <div className="flex flex-col gap-1">
              {PRICE_BANDS.map((b) => (
                <button key={b.k} onClick={() => setBand(band === b.k ? "" : b.k)}
                  className={`text-left py-1 text-sm ${band === b.k ? "text-[hsl(var(--accent))]" : "hover:text-[hsl(var(--accent))]"}`}>{b.label}</button>
              ))}
            </div>
          </FilterGroup>

          <FilterGroup label="Certification">
            <Check checked={certifiedOnly} onChange={(e) => setCertifiedOnly(e.target.checked)}>Spotter Certified</Check>
            <Check checked={bisOnly} onChange={(e) => setBisOnly(e.target.checked)}>BIS Size Match</Check>
          </FilterGroup>

          <div className="py-5">
            <p className="font-mono text-[10px] uppercase tracking-widest mb-3">Availability</p>
            <Check checked={inStockOnly} onChange={(e) => setInStockOnly(e.target.checked)}>In stock</Check>
          </div>
        </aside>

        {/* Grid */}
        <div className="md:col-span-9 lg:col-span-10 min-w-0">
          <div className="flex items-end justify-between border-b border-foreground pb-5 mb-6">
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{eyebrow}</p>
              <h1 className="font-serif text-4xl md:text-6xl tracking-tighter mt-1">{title}.</h1>
              {subtitle && <p className="mt-2 text-muted-foreground max-w-lg">{subtitle}</p>}
              {picks && pickChips.length > 0 && (
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--accent))]">{pickChips.join(" · ")}</p>
              )}
            </div>
            <div className="hidden md:flex items-center gap-6 shrink-0">
              <p className="font-mono text-[11px] text-muted-foreground whitespace-nowrap">{shown.length} product{shown.length === 1 ? "" : "s"}</p>
              <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Sort
                <select value={sort} onChange={(e) => setSort(e.target.value)} className="border border-foreground/40 bg-background px-2 py-1.5 text-foreground">
                  {SORTS.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
                </select>
              </label>
            </div>
          </div>

          {/* Collections strip — editorial jump-offs (hidden inside a collection / picks) */}
          {!collection && !picks && (
            <div className="flex gap-2 overflow-x-auto pb-2 mb-8 -mt-1">
              {COLLECTIONS.map((c) => (
                <Link key={c.slug} to={`/shop?collection=${c.slug}`}
                  className="whitespace-nowrap border border-foreground/25 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background hover:border-foreground transition-colors">
                  {c.title}
                </Link>
              ))}
            </div>
          )}
          {(collection || picks) && (
            <Link to="/shop" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-[hsl(var(--accent))] mb-8">← All products</Link>
          )}

          {/* Active filter chips */}
          {activeFilters > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-6">
              {[...sizes].map((s) => ({ label: `Size ${s}`, clear: () => toggleSize(s) }))
                .concat(band ? [{ label: PRICE_BANDS.find((b) => b.k === band)?.label, clear: () => setBand("") }] : [])
                .concat(certifiedOnly ? [{ label: "Spotter Certified", clear: () => setCertifiedOnly(false) }] : [])
                .concat(bisOnly ? [{ label: "BIS Match", clear: () => setBisOnly(false) }] : [])
                .concat(inStockOnly ? [{ label: "In stock", clear: () => setInStockOnly(false) }] : [])
                .map((c) => (
                  <button key={c.label} onClick={c.clear} className="inline-flex items-center gap-1.5 border border-foreground px-3 py-1 font-mono text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background">
                    {c.label} <span className="text-[hsl(var(--accent))]">×</span>
                  </button>
                ))}
              <button onClick={clearAll} className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-[hsl(var(--accent))] underline">Clear all</button>
            </div>
          )}

          {loading ? (
            <p className="font-mono text-sm">Loading…</p>
          ) : shown.length === 0 ? (
            <div className="py-20 text-center">
              <p className="font-serif text-3xl text-muted-foreground">Nothing matches those filters.</p>
              {activeFilters > 0 && <button onClick={clearAll} className="mt-4 font-mono text-[11px] uppercase tracking-widest border-b border-foreground pb-1 hover:text-[hsl(var(--accent))]">Clear filters</button>}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-3 gap-y-6 md:gap-6">
              {shown.map((p) => {
                const sz = p.sizes || [];
                const range = sz.length > 1 ? `${sz[0]}–${sz[sz.length - 1]}` : sz[0];
                return (
                  <Link key={p.id} to={`/product/${p.id}`} data-testid={`shop-product-${p.id}`} className="group flex flex-col md:border md:border-foreground/15 md:hover:border-foreground transition-colors">
                    <div className="relative aspect-square bg-secondary overflow-hidden">
                      {p.images?.[0] && <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
                      {p.spotted_certificate?.passed && (
                        <span className="absolute top-2 left-2 md:top-3 md:left-3 bg-background/90 border border-foreground/20 px-1.5 py-0.5 md:px-2 md:py-1 font-mono text-[7px] md:text-[8px] uppercase tracking-widest">✦ Certified</span>
                      )}
                      <div className="absolute bottom-2 right-2 md:bottom-3 md:right-3"><WishlistButton productId={p.id} /></div>
                    </div>
                    <div className="pt-2 md:p-4 flex flex-col flex-1 min-w-0">
                      <p className="font-mono text-[9px] md:text-[10px] uppercase tracking-widest text-muted-foreground truncate">{p.vendor_name}</p>
                      <h3 className="font-serif text-sm md:text-lg mt-0.5 leading-tight line-clamp-1">{p.title}</h3>
                      <p className="font-mono text-[13px] md:text-sm mt-1">₹ {Number(p.price || 0).toLocaleString("en-IN")}</p>
                      <div className="flex items-center gap-2 mt-0.5 min-w-0 font-mono text-[9px] md:text-[10px] text-muted-foreground">
                        {range && <span className="truncate">Sizes {range}</span>}
                        {p.category === "footwear" && <span className="text-[hsl(var(--accent))] shrink-0">◆ BIS</span>}
                      </div>
                      {p.rating > 0 && <p className="font-mono text-[9px] md:text-[10px] text-muted-foreground mt-0.5 md:mt-1">★ {Number(p.rating).toFixed(1)}<span className="hidden md:inline"> customer rating</span></p>}
                      <span className="mt-auto pt-2 md:pt-3 md:border-t md:border-foreground/10 font-mono text-[8px] md:text-[9px] uppercase tracking-widest text-[hsl(var(--accent))] inline-flex items-center gap-1">Why we spotted it <ArrowRight size={11} /></span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {/* Editorial discovery — turns a small catalogue into a deliberate experience */}
          {!loading && !picks && (
            <div className="mt-14 border border-foreground p-8 md:p-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Not sure what to pick?</p>
                <h3 className="font-serif text-3xl md:text-4xl tracking-tighter mt-1">Let a Spotter narrow it down.</h3>
              </div>
              <button onClick={() => openConcierge()} className="shrink-0 inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 font-mono text-[11px] uppercase tracking-widest hover:bg-[hsl(var(--accent))] hover:text-background transition-colors">
                Find my picks <ArrowRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
